import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import styles from './index.module.scss';
import { Button } from '@primitives';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import {
  CALL_BRIDGE_STATUSES,
  CALLING_MODES,
  CreateCallBridgeInteraction,
  ECallingType,
  FeedbackFormAction,
  FeedbackFormKind,
  IFeedbackForm,
  InteractionType,
  MAX_FETCH_TRY,
  WAIT_TIME_BEFORE_FETCH_INTERACTION_FEEDBACK
} from '../../interfaces/index.type';
import { ITelePhoneData } from '../../../interfaces/CaseDetail.type';
import { createKey } from '@cp/utils/CaseDetail.utils';
import Typography from '@primitives/Typography/Typography';
import { pushToSessionStorage } from '@cp/utils/StorageUtils';
import { SESSION_STORAGE_KEYS } from '@cp/constants/StorageKeys';
import { CallIcon } from '@navi/web-ui/lib/icons';
import CircularAlertIcons from 'src/assets/icons/CircularAlertIcon';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import {
  createCallBridge,
  fetchBlankDisposition,
  fetchInteraction,
  getQuestion
} from '../../actions';
import { setBlankDisposition, setSlashCallStatus } from 'src/reducers/commonSlice';
import { navigateToCaseDetails } from 'src/utils/commonUtils';
import { TAB_KEYS } from 'src/pages/CaseDetails/constants';
import APP_ROUTES from 'src/layout/Routes';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { addClickstreamEvent } from '../../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../../service/clickStream.constant';
import { ExtensionHandler } from 'src/utils/extension.utils';
import { AmeyoCallData } from 'src/service/naviExtension.service';
import { debounce } from '@navi/web-ui/lib/utils';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import { setAddedPhoneNumber } from '../../reducer/feedbackSlice';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import {
  DateFormat,
  dateFormat,
  formatToStandardTimeInHours,
  timeStringToDate
} from '@cp/src/utils/DateHelper';
import dayjs from 'dayjs';

interface CallBridgeProps {
  feedback: IFeedbackForm;
  dispatchFeedback: React.Dispatch<FeedbackFormAction>;
  loanId: string;
  customerId: string;
  phone?: string;
  inlineCallButton?: boolean;
  interactionType?: InteractionType;
  telephoneSource?: string;
  telephoneRefId?: string;
  currentCallType?: ECallingType;
  disableCallButton?: boolean;
}

const buildCreateCallBridgeRequestBody = (
  accountNumber: string,
  sourceType: string,
  recipientName: string | null,
  recipientPhoneNumber: string | undefined,
  recipientPhoneNumberCustomId: string | null,
  callingMode: string,
  telephoneReferenceId: string
): CreateCallBridgeInteraction => ({
  accountNumber,
  sourceType: `${sourceType.toUpperCase()}_PHONE_CALL`,
  recipientName,
  recipientPhoneNumber,
  recipientPhoneNumberCustomId,
  callingMode,
  referenceId: telephoneReferenceId
});

const CallBridge = (props: CallBridgeProps) => {
  const {
    feedback,
    dispatchFeedback,
    loanId,
    customerId,
    phone,
    inlineCallButton,
    interactionType,
    telephoneSource,
    telephoneRefId,
    currentCallType,
    disableCallButton
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParams = readQueryParams();
  const { caseReferenceId = '', caseBusinessVertical = '' } = useParams();

  const disableBlankDisposition = useSelector(
    (state: RootState) => state?.common?.blankDisposition?.disableBlankDisposition
  );

  const caseSnoozed = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.pause?.data?.isPaused
  );

  const user = useSelector((state: RootState) => state?.common?.userData);
  const telephones = useSelector(
    (state: RootState) => state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.telephonesv2
  );
  const editAccessFlag = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details?.data?.editAccessFlag
  );
  const isGlobalAccessGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);

  const referenceId = useSelector((store: RootState) => store?.common?.userData?.referenceId) || '';
  const blankDisposition =
    useSelector((store: RootState) => store?.common?.blankDisposition?.data) || [];

  const [ameyoCallData, setAmeyoCallData] = useState<AmeyoCallData>(
    ExtensionHandler.currentCallStatus()
  );

  const callBridgeErrorCallback = () => {
    setCallingStatus(CALL_BRIDGE_STATUSES.DEFAULT);
  };

  const [callingStatus, setCallingStatus] = React.useState(CALL_BRIDGE_STATUSES.DEFAULT);
  const [showRetryCalling, setShowRetryCalling] = React.useState(false);
  const [showRedirectionModal, setShowRedirectionModal] = React.useState(false);
  const disableCallButtons = useMemo(() => {
    if (callingStatus === CALL_BRIDGE_STATUSES.INITIATED) {
      return true;
    }
    if (currentCallType === ECallingType.AMEYO) {
      return true;
    }
    if ((ameyoCallData.ringingAt || ameyoCallData.acceptedAt) && !ameyoCallData.disconnectedAt) {
      return true;
    }
    if (callingStatus === CALL_BRIDGE_STATUSES.DEFAULT && !inlineCallButton) {
      return !feedback.telephone;
    } else {
      return (
        (!feedback.telephone || feedback.interactionId) &&
        callingStatus === (CALL_BRIDGE_STATUSES.INITIATED as CALL_BRIDGE_STATUSES)
      );
    }
  }, [callingStatus, feedback, currentCallType]);
  const disableButtons = isGlobalAccessGiven
    ? disableCallButtons || !editAccessFlag
    : disableCallButtons;

  useEffect(() => {
    dispatch(setSlashCallStatus(callingStatus));
  }, [callingStatus]);
  const counter = React.useRef(0);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | number>();

  const cleanup = () => {
    clearTimeout(timeoutRef.current);
    counter.current = 0;
  };

  const isCallInitiated = (callStatus: CALL_BRIDGE_STATUSES, interactionId: '') => {
    if (callStatus === CALL_BRIDGE_STATUSES.INITIATED) {
      setCallingStatus(CALL_BRIDGE_STATUSES.INITIATED);
      timeoutRef.current = setTimeout(
        checkInteractionStatus,
        WAIT_TIME_BEFORE_FETCH_INTERACTION_FEEDBACK,
        interactionId
      );
      counter.current += 1;
    } else if (callStatus === CALL_BRIDGE_STATUSES.SUCCESS) {
      setCallingStatus(CALL_BRIDGE_STATUSES.SUCCESS);
      cleanup();
    } else if (callStatus === CALL_BRIDGE_STATUSES.FAILED) {
      cleanup();
      setCallingStatus(CALL_BRIDGE_STATUSES.FAILED);
    } else {
      setCallingStatus(CALL_BRIDGE_STATUSES.DEFAULT);
      cleanup();
    }
  };

  const checkInteractionStatusSuccessCallback = (response: any) => {
    if (
      response.data.metadata != null &&
      response.data.metadata.callerStatus != null &&
      response.data.metadata.receiverStatus != null
    ) {
      // if (response.data.metadata.callerStatus.toUpperCase() === CALL_BRIDGE_STATUSES.ANSWER) {
      //   if (response.data.metadata.receiverStatus.toUpperCase() === CALL_BRIDGE_STATUSES.ANSWER) {
      //     setCallingStatus(CALL_BRIDGE_STATUSES.SUCCESS);
      //   } else {
      //     setCallingStatus(CALL_BRIDGE_STATUSES.CALL_SENT);
      //   }
      // } else if (
      //   response.data.metadata.callerStatus.toUpperCase() === CALL_BRIDGE_STATUSES.NO_ANSWER
      // ) {
      //   setCallingStatus(CALL_BRIDGE_STATUSES.MISSED);
      // } else {
      //   setCallingStatus(CALL_BRIDGE_STATUSES.RETRY);
      // }
      // cleanup();
      //  @TODO : map text to be shown bellow the call button with appropriate statuses
      isCallInitiated(response.data.metadata.status, response.data.referenceId);
      // cleanup();
    } else if (response.data.metadata != null) {
      if (
        response.data.metadata.status != null &&
        (response.data.metadata.status.toUpperCase() === CALL_BRIDGE_STATUSES.AGENT_NOT_READY ||
          response.data.metadata.status.toUpperCase() === CALL_BRIDGE_STATUSES.UNAUTHORISED)
      ) {
        setCallingStatus(response.data.metadata.status.toUpperCase());
        setShowRetryCalling(true);
        cleanup();
        toast('Please login to slash', { type: 'error' });
      } else {
        isCallInitiated(response.data.metadata.status, response.data.referenceId);
      }
    } else {
      timeoutRef.current = setTimeout(
        checkInteractionStatus,
        WAIT_TIME_BEFORE_FETCH_INTERACTION_FEEDBACK,
        response.data.referenceId
      );
      counter.current += 1;
    }
  };

  React.useEffect(() => {
    if (queryParams?.automatedSlashCall) {
      handleCallClick(queryParams?.automatedSlashCall.call);
      dispatchFeedback({
        type: FeedbackFormKind.UPDATE_TELEPHONES,
        payload: { telephone: queryParams?.automatedSlashCall.call }
      });
      const params = { ...queryParams };
      delete params.automatedSlashCall;
      const searchParams = createSearchParams(params);
      navigate(searchParams, { replace: true });
    }
  }, [queryParams]);

  const checkInteractionStatusFailureCallback = () => {
    cleanup();
    setCallingStatus(CALL_BRIDGE_STATUSES.RETRY);
    setShowRetryCalling(true);
  };

  const checkInteractionStatus = async (interactionReferenceId: string) => {
    if (counter.current === 0) {
      timeoutRef.current = setTimeout(() => {
        checkInteractionStatus(interactionReferenceId);
      }, WAIT_TIME_BEFORE_FETCH_INTERACTION_FEEDBACK);
      counter.current += 1;
    } else if (counter.current < MAX_FETCH_TRY) {
      fetchInteraction(
        loanId,
        customerId,
        caseReferenceId,
        caseBusinessVertical,
        checkInteractionStatusSuccessCallback,
        checkInteractionStatusFailureCallback,
        interactionReferenceId
      );
    } else {
      setCallingStatus(CALL_BRIDGE_STATUSES.RETRY);
      setShowRetryCalling(true);
      cleanup();
    }
  };

  const startCallSuccessCallback = (response: any) => {
    dispatch(
      setAddedPhoneNumber({
        phoneNumber: undefined,
        identifier: undefined
      })
    );
    dispatchFeedback({
      type: FeedbackFormKind.SET_INTERACTION_ID,
      payload: { interactionId: response?.data?.referenceId }
    });
    dispatch(
      getQuestion({
        loanaccountnumber: loanId,
        customerreferenceid: customerId,
        interactionType: ECallingType.CALL_BRIDGE
      })
    );
    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_TELEPHONES,
      payload: { telephone: response?.data?.source?.recipientNumber }
    });
    checkInteractionStatus(response?.data?.referenceId || '');
    pushToSessionStorage(SESSION_STORAGE_KEYS.INTERACTION_ID, response?.data?.referenceId);
  };

  const onStartCallClick = (numberToCall?: string) => {
    setShowRetryCalling(false);
    setCallingStatus(CALL_BRIDGE_STATUSES.INITIATED);
    const telephone: ITelePhoneData | undefined = telephones?.data?.find(
      telephone => telephone.number === numberToCall
    );
    const body: CreateCallBridgeInteraction = buildCreateCallBridgeRequestBody(
      loanId,
      telephoneSource || telephone?.source || '',
      null,
      numberToCall || telephone?.number,
      telephone?.numberCustomId || '',
      // gsmSelected ? 'CELL' : 'LANDLINE',
      CALLING_MODES.CELL,
      telephoneRefId || ''
    );
    createCallBridge(
      loanId,
      customerId,
      body,
      startCallSuccessCallback,
      interactionType,
      callBridgeErrorCallback
    );
  };

  const getCallingWindowStartTime = useSelector(
    (store: RootState) => store?.common?.callingWindow?.callingStartTime
  );

  const getCallingWindowEndTime = useSelector(
    (store: RootState) => store?.common?.callingWindow?.callingEndTime
  );

  const serverDate = useSelector((state: RootState) => state?.common?.serverDate);
  const date = dateFormat(new Date(serverDate), DateFormat.hh_mm_ss);

  const isPostMidnight = useMemo(() => {
    const time = dayjs(new Date());
    const hour = time.hour();
    return hour >= 12;
  }, []);

  const startTime = timeStringToDate(getCallingWindowStartTime);
  const endTime = timeStringToDate(getCallingWindowEndTime);
  const serverTime = timeStringToDate(date);

  const isCallingWindowDisabled = serverTime < startTime || serverTime > endTime;

  const handleCallClick = debounce((numberToCall?: string) => {
    const telephone: ITelePhoneData | undefined = telephones?.data?.find(
      telephone => telephone.number === numberToCall
    );
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_CallCTA, {
      phone: telephone?.number,
      source: telephone?.source
    });
    const callback = (data: { [key: string]: any }[]) => {
      dispatch(setBlankDisposition(data));
      const blankDisposition = data?.blankDispositions;
      if (data?.status === 'FAILED') {
        setShowRedirectionModal(true);
      } else {
        onStartCallClick(numberToCall);
      }
    };
    dispatch(fetchBlankDisposition(referenceId || '', callback));
  }, 500);

  useEffect(() => {
    if (feedback.isDefaultCallRequired) handleCallClick(feedback.telephone);
  }, [feedback]);

  const handleFillFeedbackClick = () => {
    if (blankDisposition?.length) {
      const queryParams = createQueryParams({
        blankDisposition: { interactionId: blankDisposition[0]?.referenceId }
      });
      navigateToCaseDetails(
        blankDisposition[0]?.customerId,
        blankDisposition[0]?.accountNumber,
        blankDisposition[0]?.caseReferenceId,
        blankDisposition[0]?.businessVertical,
        navigate,
        APP_ROUTES.CASE_DETAIL.path,
        queryParams,
        TAB_KEYS.COMMUNICATION_HISTORY
      );
    }
    setShowRedirectionModal(false);
  };

  const initiateCall = () => {
    if (disableCallButtons) return;
    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_TELEPHONES,
      payload: { telephone: phone }
    });
    onStartCallClick(phone);
  };

  const tooltipContent = caseSnoozed
    ? 'Calling is disabled as collections is paused for this LAN'
    : `You can call the customer from ${formatToStandardTimeInHours(getCallingWindowStartTime)} ${
        isPostMidnight ? 'tomorrow' : 'today'
      }`;

  return inlineCallButton ? (
    <CallIcon onClick={initiateCall} />
  ) : (
    <>
      <div>
        <Tooltip placement="top">
          <TooltipTrigger>
            <Button
              disabled={
                disableButtons || disableCallButton || isCallingWindowDisabled || caseSnoozed
              }
              onClick={() => {
                if (disableButtons) return;
                handleCallClick(feedback.telephone || '');
              }}
              fullWidth
              type={'submit'}
            >
              Call
            </Button>
          </TooltipTrigger>
          {isCallingWindowDisabled || caseSnoozed ? (
            <TooltipContent className="tooltipWrapper">{tooltipContent}</TooltipContent>
          ) : null}
        </Tooltip>
      </div>
      <Typography variant="p5" className={styles.lightText}>
        {(feedback?.interactionId || showRetryCalling) &&
          callingStatus === CALL_BRIDGE_STATUSES.MISSED && (
            <span className="errorText">Agent missed the call</span>
          )}
        {(feedback?.interactionId || showRetryCalling) &&
          callingStatus === CALL_BRIDGE_STATUSES.RETRY && <>Retry Calling</>}
        {(feedback?.interactionId || showRetryCalling) &&
          callingStatus === CALL_BRIDGE_STATUSES.AGENT_NOT_READY && (
            <span className="errorText">Login to slash to connect call</span>
          )}
        {(feedback?.interactionId || showRetryCalling) &&
          callingStatus === CALL_BRIDGE_STATUSES.UNAUTHORISED && (
            <span className="errorText">Retry loging into longhorn</span>
          )}
        {callingStatus === CALL_BRIDGE_STATUSES.INITIATED && <>Call Initiated...</>}
        {callingStatus === CALL_BRIDGE_STATUSES.SUCCESS && (
          <span className="successText">Call Successful</span>
        )}
        {callingStatus === CALL_BRIDGE_STATUSES.FAILED && (
          <span className="errorText">Call Failed</span>
        )}
      </Typography>
      {showRedirectionModal && !disableBlankDisposition && blankDisposition?.length
        ? ReactDOM.createPortal(
            <div className={styles.container}>
              <div className={styles.modal}>
                <div>
                  <CircularAlertIcons />
                  <Typography variant="h3" className={styles.title}>
                    Fill feedback for the previous call
                  </Typography>
                </div>
                <Typography variant="p3" className={styles.subtitle}>
                  Please fill feedback for <strong>{blankDisposition?.[0]?.name || ''}</strong>{' '}
                  before calling other customers{' '}
                </Typography>
                <Button onClick={handleFillFeedbackClick}>Fill Feedback</Button>
              </div>
            </div>,
            document.getElementById('modal-box')
          )
        : null}
    </>
  );
};

CallBridge.defaultProps = {
  loanId: '',
  customerId: ''
};

export default React.memo(CallBridge);
